import cn from 'classnames'
import { FC } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'

import { getEspActivityTypeLabel, getEspMeetingReasonLabel } from '../../../../configs/options'
import { ESP_TRANSLATION_KEYS } from '../../../../configs/translations'
import { IEspActivity } from '../../../../model/types'
import { EspBadge } from '../../../espBadge/EspBadge'

import styles from './EspActivityTimeline.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.activities

interface IEspActivityTimelineProps {
  activities: IEspActivity[]
  /** Highlights the entry currently loaded into the form panel. */
  editingActivityId: string | null
  /** Omit both to show the log read-only. */
  onEdit?: (activity: IEspActivity) => void
  onDelete?: (activity: IEspActivity) => void
}

/** The activity log as a chronological timeline (wireframe screen 4, left column). */
export const EspActivityTimeline: FC<IEspActivityTimelineProps> = ({
  activities,
  editingActivityId,
  onEdit,
  onDelete,
}) => (
  <ul className={styles.timeline}>
    {activities.map((activity) => {
      const typeLabel = getEspActivityTypeLabel(activity.activityType)

      return (
        <li
          key={activity.id}
          className={cn(styles.item, { [styles.editing]: activity.id === editingActivityId })}
        >
          <div className={styles.head}>
            <b className={styles.title}>{activity.topics || typeLabel}</b>

            <div className={styles.chips}>
              <EspBadge variant="neutral" label={typeLabel} />
              <EspBadge
                variant="neutral"
                label={getEspMeetingReasonLabel(activity.meetingReason)}
              />
            </div>
          </div>

          <div className={styles.when}>
            {new Date(activity.date).toLocaleDateString()}
            {activity.attendees ? (
              <>
                {' · '}
                <Translation
                  text={KEYS.entryWith}
                  replaceParams={[{ key: '{attendees}', value: activity.attendees }]}
                  span
                />
              </>
            ) : null}
          </div>

          {activity.outcome ? (
            <div className={styles.notes}>
              <Translation
                text={KEYS.entryOutcome}
                replaceParams={[{ key: '{outcome}', value: activity.outcome }]}
                span
              />
            </div>
          ) : null}

          {activity.notesAndDecisions ? (
            <div className={styles.notes}>{activity.notesAndDecisions}</div>
          ) : null}

          {onEdit && onDelete ? (
            <div className={styles.actions}>
              <Button
                variant="text"
                color="grey"
                size="sm"
                icon="kbase kbase-edit"
                onClick={() => onEdit(activity)}
              >
                <Translation text={KEYS.actionEdit} />
              </Button>
              <Button
                variant="text"
                color="danger"
                size="sm"
                icon="kbase kbase-delete"
                onClick={() => onDelete(activity)}
              >
                <Translation text={KEYS.actionDelete} />
              </Button>
            </div>
          ) : null}
        </li>
      )
    })}
  </ul>
)

EspActivityTimeline.displayName = 'EspActivityTimeline'
